import type { SseConnectionState, SseFrame } from '@/types/sse'

type FrameCallback = (frame: SseFrame) => void
type StateCallback = (state: SseConnectionState) => void

const isClient = typeof window !== 'undefined'

const STREAM_PATH = '/notifications/stream'
const BASE_RECONNECT_DELAY = 1000
import { versionedBaseUrl } from '@/api/api'

const MAX_RECONNECT_DELAY = 30000
/** Full jitter on the backoff so reconnecting clients do not thunder together. */
const JITTER_RATIO = 0.5

const buildStreamUrl = (): string => {
  // `VITE_API_URL` may be a path (`/api/`, the proxied stand) and may omit the version —
  // the stream lives under `/v0` like every other call, resolved against the page.
  const base = new URL(versionedBaseUrl('v0'), window.location.origin)
  base.pathname = `${base.pathname.replace(/\/$/, '')}${STREAM_PATH}`
  return base.toString()
}

/**
 * EventSource cannot carry an Authorization header and this API authenticates
 * with a bearer token, so the stream is read off fetch's body reader instead.
 */
class SseService {
  private controller: AbortController | null = null
  private frameListeners = new Set<FrameCallback>()
  private stateListeners = new Set<StateCallback>()
  private state: SseConnectionState = 'disconnected'
  private reconnectAttempts = 0
  private reconnectTimer: number | null = null
  private tokenProvider: (() => string | null) | null = null
  private stopped = true
  private lastEventId: string | null = null

  /**
   * The token is read per connection attempt rather than captured, so a refresh
   * between reconnects is picked up without restarting the service.
   */
  start(tokenProvider: () => string | null): void {
    if (!isClient) return

    this.tokenProvider = tokenProvider
    this.stopped = false
    void this.openStream()
  }

  stop(): void {
    this.stopped = true
    this.clearReconnectTimer()
    this.controller?.abort()
    this.controller = null
    this.reconnectAttempts = 0
    this.lastEventId = null
    this.setState('disconnected')
  }

  subscribe(callback: FrameCallback): () => void {
    this.frameListeners.add(callback)
    return () => {
      this.frameListeners.delete(callback)
    }
  }

  subscribeState(callback: StateCallback): () => void {
    this.stateListeners.add(callback)
    return () => {
      this.stateListeners.delete(callback)
    }
  }

  get isConnected(): boolean {
    return this.state === 'connected'
  }

  get connectionState(): SseConnectionState {
    return this.state
  }

  private async openStream(): Promise<void> {
    if (this.stopped || !isClient) return

    const token = this.tokenProvider?.() ?? null
    if (!token) {
      this.setState('disconnected')
      return
    }

    this.controller?.abort()
    this.controller = new AbortController()
    this.setState('connecting')

    try {
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`
      }
      if (this.lastEventId) {
        headers['Last-Event-ID'] = this.lastEventId
      }

      const response = await fetch(buildStreamUrl(), {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: this.controller.signal
      })

      // An expired or rejected token is not transient — retrying it forever
      // would hammer the API. The token watcher reconnects once it changes.
      if (response.status === 401 || response.status === 403) {
        console.warn(`SSE stream rejected auth (${response.status}); stopping.`)
        this.stopped = true
        this.setState('disconnected')
        return
      }

      if (!response.ok || !response.body) {
        throw new Error(`SSE stream failed with status ${response.status}`)
      }

      this.setState('connected')
      await this.readStream(response.body)

      // A clean end-of-stream is still a disconnect — the server closed us.
      this.setState('disconnected')
      this.scheduleReconnect()
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return
      }
      console.error('SSE connection error:', error)
      this.setState('disconnected')
      this.scheduleReconnect()
    }
  }

  private async readStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      await this.pumpFrames(reader, decoder, buffer)
    } finally {
      reader.releaseLock()
    }
  }

  private async pumpFrames(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder,
    buffer: string
  ): Promise<void> {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      // Backoff resets only once the stream actually delivers bytes (heartbeats
      // count). Resetting on response headers alone lets a server that accepts
      // then immediately EOFs spin at full rate forever.
      this.reconnectAttempts = 0

      buffer += decoder.decode(value, { stream: true })

      // Frames are separated by a blank line; tolerate CRLF from proxies.
      const chunks = buffer.split(/\r?\n\r?\n/)
      buffer = chunks.pop() ?? ''

      for (const chunk of chunks) {
        const frame = parseFrame(chunk)
        if (frame) {
          if (frame.id) this.lastEventId = frame.id
          this.emitFrame(frame)
        }
      }
    }
  }

  private emitFrame(frame: SseFrame): void {
    this.frameListeners.forEach((listener) => {
      try {
        listener(frame)
      } catch (listenerError) {
        console.error('SSE listener failed:', listenerError)
      }
    })
  }

  private setState(state: SseConnectionState): void {
    if (this.state === state) return
    this.state = state
    this.stateListeners.forEach((listener) => {
      try {
        listener(state)
      } catch (listenerError) {
        console.error('SSE state listener failed:', listenerError)
      }
    })
  }

  private scheduleReconnect(): void {
    if (this.stopped || !isClient) return

    this.clearReconnectTimer()

    const exponential = Math.min(
      BASE_RECONNECT_DELAY * 2 ** this.reconnectAttempts,
      MAX_RECONNECT_DELAY
    )
    const delay = exponential * (1 - JITTER_RATIO + Math.random() * JITTER_RATIO)
    this.reconnectAttempts += 1

    this.reconnectTimer = window.setTimeout(() => {
      void this.openStream()
    }, delay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null && isClient) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}

function parseFrame(chunk: string): SseFrame | null {
  const lines = chunk.split(/\r?\n/)
  let event = 'message'
  let id: string | undefined
  let retry: number | undefined
  const dataLines: string[] = []

  for (const line of lines) {
    // `:` prefixed lines are comments — servers use them as heartbeats.
    if (!line || line.startsWith(':')) continue

    const separatorIndex = line.indexOf(':')
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex)
    const rawValue = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1)
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue

    if (field === 'event') event = value
    else if (field === 'data') dataLines.push(value)
    else if (field === 'id') id = value
    else if (field === 'retry') {
      const parsed = Number(value)
      if (!Number.isNaN(parsed)) retry = parsed
    }
  }

  if (dataLines.length === 0 && event === 'message') return null

  return { event, data: dataLines.join('\n'), id, retry }
}

export const sseService = new SseService()
